/**
 * Iter287.1 — Parser de timestamps textuais para o formato estruturado
 * `{t, label}[]` esperado pelo VideoObject.hasPart do Schema.org.
 *
 * MOTIVAÇÃO
 * ---------
 * O usuário cadastra os timestamps DIRETAMENTE na descrição do vídeo do
 * YouTube (formato editorial legível para humanos), e essa descrição é
 * sincronizada crua para `episodes.description` no Mongo. Alguns poucos
 * episódios (12) têm o array estruturado `timestamps` preenchido a
 * mão, mas a grande maioria depende do parse dinâmico da descrição.
 *
 * Este utilitário centraliza a lógica em UM único lugar para que tanto:
 *   - `StructuredData.jsx` (VideoObject.hasPart → Google Key Moments)
 *   - `EpisodePage.jsx` (lista de capítulos clicáveis para o leitor)
 * consumam a mesma fonte de verdade.
 *
 * FORMATOS SUPORTADOS
 * -------------------
 * Aceita blocos em `description` com linhas do tipo:
 *
 *     00:00 – Introdução
 *     02:15 - O conceito de Governança
 *     00:14 — Abertura
 *     (01:23) Foo bar
 *     00:00:14 - Introdução (formato HH:MM:SS)
 *     12:04 – Foo.  13:15 – Bar (múltiplos na mesma linha)
 *
 * Separadores aceitos: `-`, `–`, `—`, `.`, `)`, `:`, `|`. Label mínimo
 * de 3 chars. Retorna [] quando encontra menos de 2 timestamps
 * (evita falso-positivo em citações soltas tipo "Art. 100").
 */

// Regex espelhado 1:1 do parser Python usado na auditoria (backend
// script Iter287.1). Testado contra 16 amostras de descrições reais.
const TS_RE = /(?:^|\s|\.\s|\)\s)(?:\(|\[)?(\d{1,2}:\d{2}(?::\d{2})?)(?:\)|\])?[ \t]*[-–—.):|][ \t]*([^\n\r]{2,180}?)(?=\s*\d{1,2}:\d{2}|\s*$|\n)/gm;

/**
 * Converte "HH:MM:SS" ou "MM:SS" em segundos inteiros. Retorna null
 * se o formato for inválido.
 */
function timeToSeconds(timeStr) {
  const parts = timeStr.split(":").map((x) => parseInt(x, 10));
  if (parts.some(Number.isNaN)) return null;
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return null;
}

/**
 * Extrai timestamps de um bloco de texto (descrição do YouTube).
 * Retorna `[]` se menos de 2 hits forem encontrados (para evitar
 * capturar citações jurídicas do tipo "Art. 100" ou "Lei 8.666").
 *
 * @param {string} text — descrição bruta.
 * @returns {Array<{t: number, label: string}>}
 */
export function parseTimestampsFromText(text) {
  if (!text || typeof text !== "string") return [];
  const hits = [];
  // Reset lastIndex porque TS_RE é global.
  TS_RE.lastIndex = 0;
  let m;
  while ((m = TS_RE.exec(text)) !== null) {
    const secs = timeToSeconds(m[1]);
    if (secs === null || secs < 0) continue;
    const label = (m[2] || "").trim().replace(/[.;,\s]+$/, "");
    if (label.length < 3) continue;
    hits.push({ t: secs, label });
  }
  // Dedup por (t, label[:40])
  const seen = new Set();
  const uniq = [];
  for (const h of hits) {
    const key = `${h.t}|${h.label.slice(0, 40)}`;
    if (!seen.has(key)) {
      seen.add(key);
      uniq.push(h);
    }
  }
  uniq.sort((a, b) => a.t - b.t);
  return uniq.length >= 2 ? uniq : [];
}

/**
 * Devolve o array de timestamps EFETIVO para um episódio:
 *   1. Se `episode.timestamps` existe como array com 2+ items, usa-o
 *      (curadoria manual precisa tem prioridade absoluta).
 *   2. Senão, tenta parsear `episode.description`.
 *   3. Senão, retorna [].
 *
 * @param {object} episode — doc do Mongo.
 * @returns {Array<{t: number, label: string}>}
 */
export function effectiveTimestamps(episode) {
  if (!episode) return [];
  const arr = episode.timestamps;
  if (Array.isArray(arr) && arr.length >= 2) {
    // Normaliza o mesmo shape/ordenação do parser.
    const norm = arr
      .filter((s) => s && (typeof s.t === "number" || /^\d+$/.test(String(s.t))))
      .map((s) => ({ t: Number(s.t), label: (s.label || "").trim() }))
      .filter((s) => s.label.length > 0 && s.t >= 0)
      .sort((a, b) => a.t - b.t);
    if (norm.length >= 2) return norm;
  }
  return parseTimestampsFromText(episode.description || "");
}
